import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import Step0DeliveryType from './Step0DeliveryType';
import Step1Arrangement from './Step1Arrangement';
import Step1DesignPicker from './Step1DesignPicker';
import Step2Delivery from './Step2Delivery';
import Step2Addons from './Step2Addons';
import Step3Customer from './Step3Customer';
import Step4Review from './Step4Review';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const STORAGE_KEY = 'az_roses_cart';

const DISPLAY_STEPS = [
  { label: 'Delivery',     display: 1, stepKey: '0' },
  { label: 'Design',       display: 2, stepKey: '1a' },
  { label: 'Arrangement',  display: 3, stepKey: '1b' },
  { label: 'Add-ons',      display: 4, stepKey: 'addons' },
  { label: 'Details',      display: 5, stepKey: 2 },
  { label: 'Review',       display: 6, stepKey: 4 },
];

function getDisplayStep(step) {
  if (step === '0')      return 1;
  if (step === '1a')     return 2;
  if (step === '1b')     return 3;
  if (step === 'addons') return 4;
  if (step === 2)        return 5;
  return 6;
}

export default function MultiStepForm({ primaryColor }) {
  const [step, setStep] = useState('0');
  const [formData, setFormData] = useState({ addons: [] });
  const [session, setSession] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [cartRestored, setCartRestored] = useState(false);

  // ── Restore cart from sessionStorage on mount ──────────────────────────
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { savedStep, savedFormData } = JSON.parse(saved);
        if (savedFormData && Object.keys(savedFormData).length > 1) {
          setFormData(savedFormData);
          if (savedStep) setStep(savedStep);
          setCartRestored(true);
        }
      }
    } catch (e) {
      console.warn('Could not restore cart:', e);
    }
  }, []);

  // ── Persist cart to sessionStorage on every change ─────────────────────
  useEffect(() => {
    try {
      // Don't persist if cart is essentially empty
      if (Object.keys(formData).length <= 1) return;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ savedStep: step, savedFormData: formData }));
    } catch (e) {
      console.warn('Could not save cart:', e);
    }
  }, [formData, step]);

  // ── Auth + profile prefill ─────────────────────────────────────────────
  useEffect(() => {
    async function initSession() {
      if (!isSupabaseConfigured()) return;
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);

        if (currentSession) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, email, phone')
            .eq('id', currentSession.user.id)
            .single();

          if (profile) {
            setFormData(prev => ({
              ...prev,
              customer_name:  profile.full_name || prev.customer_name || '',
              customer_email: profile.email     || prev.customer_email || '',
              confirm_email:  profile.email     || prev.confirm_email || '',
              customer_phone: profile.phone     || prev.customer_phone || '',
            }));
          }
        }
      } catch (err) {
        console.error('Session/profile init failed:', err);
      } finally {
        setProfileLoaded(true);
      }
    }

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      // When user signs in mid-form, prefill their profile without wiping cart
      if (sess) {
        supabase
          .from('profiles')
          .select('full_name, email, phone')
          .eq('id', sess.user.id)
          .single()
          .then(({ data: profile }) => {
            if (profile) {
              setFormData(prev => ({
                ...prev,
                customer_name:  profile.full_name || prev.customer_name || '',
                customer_email: profile.email     || prev.customer_email || '',
                confirm_email:  profile.email     || prev.confirm_email || '',
                customer_phone: profile.phone     || prev.customer_phone || '',
              }));
            }
          });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const updateFormData = (newData) => setFormData(prev => ({ ...prev, ...newData }));

  // Clear cart from storage when order is complete
  const clearCart = () => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  };

  const displayStep = getDisplayStep(step);

  // Clickable step — only allow navigating back to completed steps
  const handleStepClick = (s) => {
    const targetDisplay = s.display;
    if (targetDisplay >= displayStep) return; // Can't jump forward
    setStep(s.stepKey);
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 md:px-10 py-8 md:py-16">

      {/* Cart restored banner */}
      {cartRestored && (
        <div className="mb-6 max-w-lg mx-auto bg-blush-50 border border-blush-100 rounded-2xl px-5 py-3 flex items-center justify-between gap-3">
          <p className="text-sm text-sage-600 font-medium">
            Welcome back — your cart has been restored.
          </p>
          <button
            onClick={() => {
              clearCart();
              setFormData({ addons: [] });
              setStep('0');
              setCartRestored(false);
            }}
            className="text-xs text-blush-400 font-bold hover:text-blush-500 shrink-0"
          >
            Start over
          </button>
        </div>
      )}

      {/* Progress indicators — clickable for completed steps */}
      <div className="mb-12 flex justify-center items-center max-w-lg mx-auto">
        {DISPLAY_STEPS.map((s, i) => {
          const isCompleted = displayStep > s.display;
          const isCurrent   = displayStep === s.display;
          const isClickable = isCompleted;
          return (
            <React.Fragment key={s.display}>
              <div className="flex flex-col items-center gap-1.5 relative group">
                <button
                  onClick={() => handleStepClick(s)}
                  disabled={!isClickable}
                  title={s.label}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                    isCurrent   ? 'bg-blush-300 text-white shadow-glow scale-110' :
                    isCompleted ? 'bg-blush-200 text-white hover:bg-blush-400 cursor-pointer hover:scale-110' :
                                  'bg-sage-100 text-sage-400 cursor-default'
                  }`}
                >
                  {isCompleted ? '✓' : s.display}
                </button>
                {/* Label tooltip on hover for completed steps */}
                {isClickable && (
                  <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] font-bold text-blush-400 uppercase tracking-wider whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {s.label}
                  </span>
                )}
              </div>
              {i < DISPLAY_STEPS.length - 1 && (
                <div className={`h-1 flex-1 mx-2 rounded-full transition-colors duration-500 ${displayStep > s.display ? 'bg-blush-200' : 'bg-sage-100'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="relative max-w-3xl mx-auto">
        <AnimatePresence mode="wait">
          {step === '0' && (
            <Step0DeliveryType
              key="step0"
              formData={formData}
              updateFormData={updateFormData}
              onNext={() => setStep('1a')}
            />
          )}
          {step === '1a' && (
            <Step1DesignPicker
              key="step1a"
              formData={formData}
              updateFormData={updateFormData}
              onNext={() => setStep('1b')}
              onBack={() => setStep('0')}
            />
          )}
          {step === '1b' && (
            <Step1Arrangement
              key="step1b"
              formData={formData}
              updateFormData={updateFormData}
              onNext={() => setStep('addons')}
              onBack={() => setStep('1a')}
            />
          )}
          {step === 'addons' && (
            <Step2Addons
              key="addons"
              formData={formData}
              updateFormData={updateFormData}
              onNext={() => setStep(2)}
              onBack={() => setStep('1b')}
            />
          )}
          {step === 2 && (
            <Step2Delivery
              key="step2"
              formData={formData}
              updateFormData={updateFormData}
              onNext={() => setStep(3)}
              onBack={() => setStep('addons')}
            />
          )}
          {step === 3 && (
            <Step3Customer
              key="step3"
              formData={formData}
              updateFormData={updateFormData}
              onNext={() => setStep(4)}
              onBack={() => setStep(2)}
              session={session}
              profileLoaded={profileLoaded}
            />
          )}
          {step === 4 && (
            <Step4Review
              key="step4"
              formData={formData}
              setStep={setStep}
              onBack={() => setStep(3)}
              session={session}
              onOrderComplete={clearCart}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
